/**
 * Audit Log Page
 * Complete audit trail for financial transactions
 * For accountants and auditors to review all activities
 */

import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  Search,
  Download,
  FileText,
  Shield,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { AdminUser } from '@/lib/authService'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import { formatCurrency } from '@/lib/currency'

interface AuditLogProps {
  user: AdminUser | null
}

interface JournalEntry {
  id: string
  entry_number: string
  entry_date: string
  description: string
  status: string
  created_at: string
  created_by: string
  lines: JournalEntryLine[]
}

interface JournalEntryLine {
  id: string
  account_id: string
  debit_amount: number
  credit_amount: number
  description: string
  account: {
    account_code: string
    account_name: string
    account_type: string
  }
}

interface Contribution {
  id: string
  contributor_name: string
  contribution_type: string
  amount: number
  currency: string
  status: string
  is_posted_to_ledger: boolean
  journal_entry_id: string | null
  contribution_date: string
  created_at: string
}

export default function AuditLog({ user }: AuditLogProps) {
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])
  const [contributions, setContributions] = useState<Contribution[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  useEffect(() => {
    if (user?.churchId) {
      loadAuditData()
    }
  }, [user, filterType, startDate, endDate])

  async function loadAuditData() {
    try {
      setLoading(true)
      await Promise.all([
        loadJournalEntries(),
        loadContributions()
      ])
    } catch (error) {
      console.error('Error loading audit data:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadJournalEntries() {
    try {
      let query = supabase
        .from('general_ledger')
        .select(`
          *,
          lines:general_ledger_lines(
            *,
            account:chart_of_accounts(account_code, account_name, account_type)
          )
        `)
        .eq('church_id', user!.churchId)
        .order('entry_date', { ascending: false })

      if (startDate) {
        query = query.gte('entry_date', startDate)
      }

      if (endDate) {
        query = query.lte('entry_date', endDate)
      }

      const { data, error } = await query.limit(100)

      if (error) throw error
      setJournalEntries(data || [])
    } catch (error) {
      console.error('Error loading journal entries:', error)
    }
  }

  async function loadContributions() {
    try {
      let query = supabase
        .from('contributions')
        .select('*')
        .eq('church_id', user!.churchId)
        .order('contribution_date', { ascending: false })

      if (filterType !== 'all') {
        query = query.eq('contribution_type', filterType)
      }

      if (startDate) {
        query = query.gte('contribution_date', startDate)
      }

      if (endDate) {
        query = query.lte('contribution_date', endDate)
      }

      const { data, error } = await query.limit(100)

      if (error) throw error
      setContributions(data || [])
    } catch (error) {
      console.error('Error loading contributions:', error)
    }
  }

  function exportToCSV() {
    // TODO: Implement CSV export
    alert('CSV export will be implemented')
  }

  const filteredJournalEntries = journalEntries.filter(je =>
    je.entry_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    je.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const unpostedContributions = contributions.filter(c => !c.is_posted_to_ledger)
  const voidedContributions = contributions.filter(c => c.status === 'voided')

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Audit Log</h1>
            <p className="text-sm text-muted-foreground">
              Complete audit trail of all financial transactions
            </p>
          </div>
          <Button onClick={exportToCSV}>
            <Download className="mr-2 h-4 w-4" />
            Export to CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="border-b bg-background px-6 py-4">
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Journal Entries
              </CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{journalEntries.length}</div>
              <p className="text-xs text-muted-foreground">
                Posted transactions
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Unposted Contributions
              </CardTitle>
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">
                {unpostedContributions.length}
              </div>
              <p className="text-xs text-muted-foreground">
                Need attention
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Voided Transactions
              </CardTitle>
              <Shield className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                {voidedContributions.length}
              </div>
              <p className="text-xs text-muted-foreground">
                Reversed entries
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b bg-background px-6 py-4">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="grid gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              placeholder="Start Date"
            />
          </div>

          <div className="grid gap-2">
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="End Date"
            />
          </div>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger>
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="tithe">Tithe</SelectItem>
              <SelectItem value="offering">Offering</SelectItem>
              <SelectItem value="donation">Donation</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <Tabs defaultValue="journal" className="w-full">
          <TabsList>
            <TabsTrigger value="journal">
              <FileText className="mr-2 h-4 w-4" />
              Journal Entries
            </TabsTrigger>
            <TabsTrigger value="contributions">
              <Shield className="mr-2 h-4 w-4" />
              Contributions
            </TabsTrigger>
            <TabsTrigger value="issues">
              <AlertCircle className="mr-2 h-4 w-4" />
              Issues
              {unpostedContributions.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {unpostedContributions.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Journal Entries Tab */}
          <TabsContent value="journal" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>General Ledger - Journal Entries</CardTitle>
                <CardDescription>
                  All posted journal entries with double-entry details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredJournalEntries.map((entry) => (
                    <Card key={entry.id} className="border-2">
                      <CardHeader className="bg-muted/50">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base">
                              {entry.entry_number}
                            </CardTitle>
                            <CardDescription>
                              {format(new Date(entry.entry_date), 'MMM dd, yyyy')} - {entry.description}
                            </CardDescription>
                          </div>
                          <Badge>{entry.status}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Account</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead className="text-right">Debit</TableHead>
                              <TableHead className="text-right">Credit</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {entry.lines?.map((line) => (
                              <TableRow key={line.id}>
                                <TableCell>
                                  <div>
                                    <div className="font-medium">
                                      {line.account.account_code} - {line.account.account_name}
                                    </div>
                                    <div className="text-xs text-muted-foreground capitalize">
                                      {line.account.account_type}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {line.description}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {line.debit_amount > 0 ? formatCurrency(line.debit_amount, 'GHS') : '-'}
                                </TableCell>
                                <TableCell className="text-right font-medium">
                                  {line.credit_amount > 0 ? formatCurrency(line.credit_amount, 'GHS') : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow className="bg-muted/50 font-bold">
                              <TableCell colSpan={2}>Total</TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  entry.lines?.reduce((sum, line) => sum + line.debit_amount, 0) || 0,
                                  'GHS'
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(
                                  entry.lines?.reduce((sum, line) => sum + line.credit_amount, 0) || 0,
                                  'GHS'
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}

                  {filteredJournalEntries.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No journal entries found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contributions Tab */}
          <TabsContent value="contributions" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Contribution Audit Trail</CardTitle>
                <CardDescription>
                  All contributions with posting status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Contributor</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Posted to Ledger</TableHead>
                      <TableHead>Journal Entry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contributions.map((contribution) => (
                      <TableRow key={contribution.id} className={contribution.status === 'voided' ? 'bg-destructive/10' : ''}>
                        <TableCell>
                          {format(new Date(contribution.contribution_date), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>{contribution.contributor_name || 'Anonymous'}</TableCell>
                        <TableCell className="capitalize">
                          {contribution.contribution_type.replace('_', ' ')}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(contribution.amount, contribution.currency || 'GHS')}
                        </TableCell>
                        <TableCell>
                          <Badge variant={contribution.status === 'voided' ? 'destructive' : 'default'}>
                            {contribution.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {contribution.is_posted_to_ledger ? (
                            <Badge variant="secondary">
                              <CheckCircle className="mr-1 h-3 w-3" />
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <AlertCircle className="mr-1 h-3 w-3" />
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {contribution.journal_entry_id ?
                            journalEntries.find(je => je.id === contribution.journal_entry_id)?.entry_number ||
                            contribution.journal_entry_id.substring(0, 8) :
                            '-'
                          }
                        </TableCell>
                      </TableRow>
                    ))}

                    {contributions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No contributions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues" className="mt-6">
            <div className="space-y-4">
              {/* Unposted Contributions */}
              {unpostedContributions.length > 0 && (
                <Card className="border-destructive">
                  <CardHeader>
                    <CardTitle className="text-destructive">
                      <AlertCircle className="inline-block mr-2 h-5 w-5" />
                      Unposted Contributions
                    </CardTitle>
                    <CardDescription>
                      These contributions have not been posted to the general ledger
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Contributor</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unpostedContributions.map((contribution) => (
                          <TableRow key={contribution.id}>
                            <TableCell>
                              {format(new Date(contribution.contribution_date), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>{contribution.contributor_name || 'Anonymous'}</TableCell>
                            <TableCell className="capitalize">
                              {contribution.contribution_type.replace('_', ' ')}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(contribution.amount, contribution.currency || 'GHS')}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {format(new Date(contribution.created_at), 'MMM dd, yyyy HH:mm')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Voided Contributions */}
              {voidedContributions.length > 0 && (
                <Card className="border-yellow-600">
                  <CardHeader>
                    <CardTitle className="text-yellow-900">
                      <Shield className="inline-block mr-2 h-5 w-5" />
                      Voided/Reversed Contributions
                    </CardTitle>
                    <CardDescription>
                      These contributions have been voided with reversing entries
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Contributor</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Journal Entry</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {voidedContributions.map((contribution) => (
                          <TableRow key={contribution.id}>
                            <TableCell>
                              {format(new Date(contribution.contribution_date), 'MMM dd, yyyy')}
                            </TableCell>
                            <TableCell>{contribution.contributor_name || 'Anonymous'}</TableCell>
                            <TableCell className="capitalize">
                              {contribution.contribution_type.replace('_', ' ')}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(contribution.amount, contribution.currency || 'GHS')}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {contribution.journal_entry_id ?
                                journalEntries.find(je => je.id === contribution.journal_entry_id)?.entry_number ||
                                contribution.journal_entry_id.substring(0, 8) :
                                '-'
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {unpostedContributions.length === 0 && voidedContributions.length === 0 && (
                <Card>
                  <CardContent className="text-center py-8">
                    <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                    <p className="text-lg font-medium">No Issues Found</p>
                    <p className="text-sm text-muted-foreground">
                      All contributions are properly posted to the ledger
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
